<template>
  <div class="cat-search" ref="wrapper">

    <div class="input-group input-group-sm">
      <input
        ref="inputEl"
        type="text"
        class="form-control form-control-sm"
        :placeholder="placeholder"
        :disabled="disabled"
        v-model="texto"
        autocomplete="off"
        @input="onInput"
        @focus="onFocus"
        @keydown.enter.prevent="seleccionarPrimero"
        @keydown.escape="cerrar"
        @keydown.down.prevent="moverResaltado(1)"
        @keydown.up.prevent="moverResaltado(-1)"
        @blur="onBlurInput"
      />
      <button
        v-if="texto"
        type="button"
        class="btn btn-outline-secondary btn-sm px-2"
        :disabled="disabled"
        tabindex="-1"
        title="Limpiar"
        @click="limpiar"
      >
        <i class="bi bi-x"></i>
      </button>
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm px-2"
        :disabled="disabled"
        tabindex="-1"
        title="Buscar en catálogo"
        @click="abrirModal"
      >
        <i class="bi bi-search"></i>
      </button>
      <span v-if="buscando" class="input-group-text py-0 px-2 bg-white">
        <span class="spinner-border spinner-border-sm text-secondary" style="width:.75rem;height:.75rem"></span>
      </span>
    </div>

    <!-- Dropdown inline -->
    <Teleport to="body">
      <div v-if="abierto" class="cat-search-dropdown shadow-sm" :style="dropdownStyle">
        <template v-if="resultados.length > 0">
          <div
            v-for="(item, i) in resultados"
            :key="item.Clave"
            class="cat-search-item"
            :class="{ 'cat-search-item--activo': i === resaltado }"
            @mousedown.prevent="seleccionar(item)"
            @mouseover="resaltado = i"
          >
            <span class="cat-item-clave">{{ item.Clave }}</span>
            <span class="cat-item-nombre text-truncate">{{ itemNombre(item) }}</span>
          </div>
        </template>
        <div v-else-if="!buscando" class="cat-search-empty text-muted small px-3 py-2">
          <div>Sin resultados para "<em>{{ texto }}</em>"</div>
          <div v-if="esCatalogoMaterial" class="mt-1" style="white-space: normal; line-height: 1.3;">
            Si el artículo no aparece, puede estar inactivo o sin el checklist de compras en SAP. Favor de consultar con el encargado del catálogo.
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Modal de búsqueda -->
    <Teleport to="body">
      <div v-if="modalAbierto" class="modal-backdrop-custom">
        <div class="modal-dialogo-custom">

          <!-- Header -->
          <div class="modal-header-custom">
            <span class="fw-semibold">{{ placeholder || 'Buscar' }}</span>
            <button type="button" class="btn-close btn-close-white" @click="cerrarModal"></button>
          </div>

          <!-- Buscador -->
          <div class="modal-body-custom px-3 pt-3 pb-2">
            <input
              ref="modalInput"
              type="text"
              class="form-control form-control-sm"
              placeholder="Escribir para filtrar..."
              v-model="modalTexto"
              autocomplete="off"
              @input="onModalInput"
            />
          </div>

          <!-- Tabla -->
          <div class="modal-tabla-wrap">
            <div v-if="modalBuscando" class="text-center py-4">
              <span class="spinner-border spinner-border-sm text-primary me-2"></span>Cargando...
            </div>
            <div v-else-if="modalResultados.length === 0" class="text-center text-muted py-4 small px-3">
              <div>Sin resultados</div>
              <div v-if="esCatalogoMaterial" class="mt-2" style="white-space: normal; line-height: 1.35;">
                Si el artículo no aparece, puede estar inactivo o sin el checklist de compras en SAP. Favor de consultar con el encargado del catálogo.
              </div>
            </div>
            <table v-else class="table table-hover table-sm mb-0">
              <thead class="table-light">
                <tr>
                  <th class="ps-3" style="width:120px">Clave</th>
                  <th>Nombre</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in modalPagina"
                  :key="item.Clave"
                  class="cat-modal-row"
                  @click="seleccionarModal(item)"
                >
                  <td class="ps-3 fw-semibold text-primary">{{ item.Clave }}</td>
                  <td>{{ itemNombre(item) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Footer / paginación -->
          <div class="modal-footer-custom d-flex justify-content-between align-items-center px-3 py-2">
            <small class="text-muted">
              {{ modalResultados.length }} registro(s)
              <span v-if="modalTotalPaginas > 1"> — página {{ modalPaginaActual }} de {{ modalTotalPaginas }}</span>
            </small>
            <nav v-if="modalTotalPaginas > 1">
              <ul class="pagination pagination-sm mb-0">
                <li class="page-item" :class="{ disabled: modalPaginaActual === 1 }">
                  <button class="page-link" @click="modalPaginaActual--">‹</button>
                </li>
                <li
                  v-for="p in modalPaginasVisibles"
                  :key="p"
                  class="page-item"
                  :class="{ active: p === modalPaginaActual }"
                >
                  <button class="page-link" @click="modalPaginaActual = p">{{ p }}</button>
                </li>
                <li class="page-item" :class="{ disabled: modalPaginaActual === modalTotalPaginas }">
                  <button class="page-link" @click="modalPaginaActual++">›</button>
                </li>
              </ul>
            </nav>
          </div>

        </div>
      </div>
    </Teleport>

  </div>
</template>

<script>
import catalogosViewService from '@/shared/services/catalogosView.service'

export default {
  name: 'CatSearch',

  props: {
    modelValue: { type: String, default: '' },
    catalogo:   { type: String, required: true },
    placeholder:{ type: String, default: 'Buscar...' },
    disabled:   { type: Boolean, default: false },
    minLength:  { type: Number, default: 3 },
    displayField: { type: String, default: 'Clave' },
    /** Nombre amigable de la selección actual (p.ej. ArticuloNombre / SocioNegocioNombre). */
    label: { type: String, default: '' },
    /** Si true: muestra "CLAVE — Nombre" cuando hay nombre. */
    showClaveNombre: { type: Boolean, default: true },
    /** Si true: no acepta texto libre; solo claves del catálogo (o exact match al salir). */
    soloCatalogo: { type: Boolean, default: false }
  },

  emits: ['update:modelValue', 'select', 'invalid'],

  data() {
    return {
      // --- dropdown inline ---
      texto:      this.modelValue || '',
      selectedItem: null,
      resultados: [],
      buscando:   false,
      abierto:    false,
      resaltado:  -1,
      timer:      null,
      dropdownStyle: {},

      // --- modal ---
      modalAbierto:      false,
      modalTexto:        '',
      modalResultados:   [],
      modalBuscando:     false,
      modalTimer:        null,
      modalPaginaActual: 1,
      modalPorPagina:    15,
    }
  },

  computed: {
    esCatU() { return this.catalogo.startsWith('catu_') },
    catalogoNombre() { return this.esCatU ? this.catalogo.slice(5) : this.catalogo },
    esCatalogoMaterial() {
      const c = String(this.catalogoNombre || '').toLowerCase()
      return c === 'material' || c === 'articulo' || c === 'artículo'
    },

    modalTotalPaginas() {
      return Math.max(1, Math.ceil(this.modalResultados.length / this.modalPorPagina))
    },
    modalPagina() {
      const ini = (this.modalPaginaActual - 1) * this.modalPorPagina
      return this.modalResultados.slice(ini, ini + this.modalPorPagina)
    },
    modalPaginasVisibles() {
      const desde = Math.max(1, this.modalPaginaActual - 2)
      const hasta = Math.min(this.modalTotalPaginas, desde + 4)
      const rango = []
      for (let i = desde; i <= hasta; i++) rango.push(i)
      return rango
    }
  },

  watch: {
    modelValue() {
      this.syncTextoDesdeProps()
    },
    label() {
      this.syncTextoDesdeProps()
    }
  },

  mounted() {
    this.syncTextoDesdeProps()
    document.addEventListener('mousedown', this.clickFuera)
    window.addEventListener('scroll', this.recalcularPos, true)
    window.addEventListener('resize', this.recalcularPos)
    document.addEventListener('keydown', this.onKeyGlobal)
  },

  beforeUnmount() {
    document.removeEventListener('mousedown', this.clickFuera)
    window.removeEventListener('scroll', this.recalcularPos, true)
    window.removeEventListener('resize', this.recalcularPos)
    document.removeEventListener('keydown', this.onKeyGlobal)
    clearTimeout(this.timer)
    clearTimeout(this.modalTimer)
  },

  methods: {
    // ── Dropdown inline ───────────────────────────────────────────────────────
    recalcularPos() {
      if (!this.abierto || !this.$refs.wrapper) return
      const rect = this.$refs.wrapper.getBoundingClientRect()
      this.dropdownStyle = {
        position:     'fixed',
        top:          rect.bottom + 2 + 'px',
        left:         rect.left + 'px',
        minWidth:     rect.width + 'px',
        maxWidth:     '420px',
        maxHeight:    '260px',
        overflowY:    'auto',
        background:   '#fff',
        border:       '1px solid #dee2e6',
        borderRadius: '0.375rem',
        zIndex:       '9999'
      }
    },

    onInput() {
      this.selectedItem = null
      clearTimeout(this.timer)
      this.resaltado = -1
      // Si el usuario edita un "CLAVE — Nombre" ya mostrado, al modelo solo va la clave
      const valor = this.extraerClaveDeTexto(this.texto)
      if (this.texto.length < this.minLength) {
        this.resultados = []
        this.abierto    = false
        // Con soloCatalogo no propagar texto libre (evita guardar basura / defaults raros)
        if (!this.soloCatalogo) this.$emit('update:modelValue', valor)
        return
      }
      this.timer = setTimeout(() => this.buscar(), 300)
      if (!this.soloCatalogo) this.$emit('update:modelValue', valor)
    },

    onFocus() {
      if (this.texto.length >= this.minLength && this.resultados.length > 0) {
        this.recalcularPos()
        this.abierto = true
      }
    },

    onBlurInput() {
      if (!this.soloCatalogo) return
      // Esperar click en dropdown antes de validar
      setTimeout(() => {
        if (!this.abierto) this.resolverAlSalir()
      }, 180)
    },

    async resolverAlSalir() {
      if (!this.soloCatalogo || this.disabled) return
      const typedRaw = String(this.texto || '').trim()
      if (!typedRaw) {
        this.selectedItem = null
        this.$emit('update:modelValue', '')
        this.$emit('select', null)
        return
      }
      const typedClave = this.extraerClaveDeTexto(typedRaw)
      if (this.selectedItem) {
        const selClave = String(this.itemClave(this.selectedItem) || '').toUpperCase()
        const display = this.getDisplayValue(this.selectedItem)
        if (
          selClave
          && (
            typedRaw.toUpperCase() === selClave
            || typedClave.toUpperCase() === selClave
            || typedRaw === display
          )
        ) {
          this.texto = display
          this.$emit('update:modelValue', this.itemClave(this.selectedItem))
          return
        }
      }
      // Match exacto por Clave (ej. escribió USD y está en catálogo)
      try {
        const lista = this.esCatU
          ? await catalogosViewService.buscarU(this.catalogoNombre, typedClave, 0)
          : await catalogosViewService.buscar(this.catalogo, typedClave, 0)
        const match = (lista || []).find(i => {
          const c = String(i?.Clave || '').toUpperCase()
          const t = typedClave.toUpperCase()
          return c === t || (t === 'MXN' && c === 'MXP') || (t === 'MXP' && c === 'MXN')
        })
        if (match) {
          // Si escribió/default MXN y el catálogo trae MXP (o viceversa), conservar lo que el usuario tiene
          if (
            (typedClave.toUpperCase() === 'MXN' && String(match.Clave).toUpperCase() === 'MXP') ||
            (typedClave.toUpperCase() === 'MXP' && String(match.Clave).toUpperCase() === 'MXN')
          ) {
            this.selectedItem = { ...match, Clave: typedClave.toUpperCase() }
            this.texto = this.getDisplayValue(this.selectedItem)
            this.$emit('update:modelValue', typedClave.toUpperCase())
            this.$emit('select', this.selectedItem)
            return
          }
          this.seleccionar(match)
          return
        }
      } catch (_) { /* ignore */ }
      // Inválida: no dejar el texto suelto
      this.selectedItem = null
      this.texto = ''
      this.resultados = []
      this.$emit('update:modelValue', '')
      this.$emit('select', null)
      this.$emit('invalid', typedRaw)
    },

    /** Si el input muestra "CLAVE — Nombre", toma solo la clave para validar. */
    extraerClaveDeTexto(texto) {
      const t = String(texto || '').trim()
      const sep = t.indexOf(' — ')
      if (sep > 0) return t.slice(0, sep).trim()
      return t
    },

    async buscar() {
      this.recalcularPos()
      this.buscando = true
      this.abierto  = true
      try {
        const filtro = this.extraerClaveDeTexto(this.texto)
        const raw = this.esCatU
          ? await catalogosViewService.buscarU(this.catalogoNombre, filtro, this.minLength)
          : await catalogosViewService.buscar(this.catalogo, filtro, this.minLength)
        this.resultados = this.filtrarResultadosCatalogo(raw)
        this.resaltado  = this.resultados.length > 0 ? 0 : -1
      } catch (e) {
        this.resultados = []
      } finally {
        this.buscando = false
      }
    },

    /**
     * Materiales: solo activos y de compra (si el API trae esas banderas).
     * Evita elegir artículos que luego fallan en SAP por checklist de compras.
     */
    filtrarResultadosCatalogo(lista) {
      const arr = Array.isArray(lista) ? lista : []
      if (!this.esCatalogoMaterial) return arr
      return arr.filter(item => {
        if (!item || typeof item !== 'object') return false
        const activo = item.EsActivo ?? item.esActivo ?? item.Esactivo
        const compra = item.EsDeCompra ?? item.esDeCompra ?? item.Esdecompra
        if (activo === false || activo === 0 || activo === '0' || activo === 'N') return false
        if (compra === false || compra === 0 || compra === '0' || compra === 'N') return false
        return true
      })
    },

    itemClave(item) {
      if (!item || typeof item !== 'object') return ''
      const c = item.Clave ?? item.clave
      return c == null ? '' : String(c)
    },

    itemNombre(item) {
      if (!item || typeof item !== 'object') return ''
      return String(item.Alias || item.Nombre || item.nombre || item.Descripcion || item.descripcion || '').trim()
    },

    formatClaveNombre(clave, nombre) {
      const c = String(clave || '').trim()
      const n = String(nombre || '').trim()
      if (this.showClaveNombre && c && n) return `${c} — ${n}`
      return c || n || ''
    },

    textoDesdeModelo() {
      return this.formatClaveNombre(this.modelValue, this.label)
    },

    syncTextoDesdeProps() {
      if (this.abierto) return
      const input = this.$refs.inputEl
      if (input && document.activeElement === input) return
      if (this.selectedItem && this.itemClave(this.selectedItem) === String(this.modelValue || '')) {
        this.texto = this.getDisplayValue(this.selectedItem)
        return
      }
      this.texto = this.textoDesdeModelo()
    },

    seleccionar(item) {
      this.selectedItem = item
      this.texto      = this.getDisplayValue(item)
      this.abierto    = false
      this.resultados = []
      this.$emit('update:modelValue', this.itemClave(item))
      this.$emit('select', item)
    },

    seleccionarPrimero() {
      const idx = this.resaltado >= 0 ? this.resaltado : 0
      if (this.resultados[idx]) {
        this.seleccionar(this.resultados[idx])
      } else if (this.texto.length >= this.minLength) {
        this.buscar()
      }
    },

    moverResaltado(dir) {
      if (!this.resultados.length) return
      this.resaltado = Math.max(0, Math.min(this.resultados.length - 1, this.resaltado + dir))
    },

    limpiar() {
      this.selectedItem = null
      this.texto      = ''
      this.resultados = []
      this.abierto    = false
      this.$emit('update:modelValue', '')
      this.$emit('select', null)
    },

    getDisplayValue(item) {
      if (!item) return ''
      const clave = this.itemClave(item)
      const nombre = this.itemNombre(item)
      if (String(this.displayField || '').toLowerCase() === 'nombre') {
        return nombre || clave || ''
      }
      return this.formatClaveNombre(clave, nombre)
    },

    cerrar() {
      this.abierto   = false
      this.resaltado = -1
    },

    clickFuera(e) {
      const wrapper  = this.$refs.wrapper
      const dropdown = document.querySelector('.cat-search-dropdown')
      if (wrapper && !wrapper.contains(e.target) && dropdown && !dropdown.contains(e.target)) {
        this.cerrar()
        if (this.soloCatalogo) this.resolverAlSalir()
      } else if (wrapper && !wrapper.contains(e.target) && !dropdown) {
        this.cerrar()
        if (this.soloCatalogo) this.resolverAlSalir()
      }
    },

    // ── Modal ─────────────────────────────────────────────────────────────────
    async abrirModal() {
      this.modalTexto        = ''
      this.modalResultados   = []
      this.modalPaginaActual = 1
      this.modalAbierto      = true
      await this.$nextTick()
      this.$refs.modalInput?.focus()
      await this.buscarModal('')
    },

    cerrarModal() {
      this.modalAbierto = false
      clearTimeout(this.modalTimer)
    },

    onModalInput() {
      clearTimeout(this.modalTimer)
      this.modalPaginaActual = 1
      this.modalTimer = setTimeout(() => this.buscarModal(this.modalTexto), 300)
    },

    async buscarModal(texto) {
      this.modalBuscando = true
      try {
        const raw = this.esCatU
          ? await catalogosViewService.buscarU(this.catalogoNombre, texto, 0)
          : await catalogosViewService.buscar(this.catalogo, texto, 0)
        this.modalResultados = this.filtrarResultadosCatalogo(raw)
      } catch (e) {
        this.modalResultados = []
      } finally {
        this.modalBuscando = false
      }
    },

    seleccionarModal(item) {
      this.cerrarModal()
      this.seleccionar(item)
    },

    onKeyGlobal(e) {
      if (e.key === 'Escape' && this.modalAbierto) this.cerrarModal()
    }
  }
}
</script>

<style>
/* Sin scoped — dropdown y modal están en body via Teleport */

.cat-search-dropdown {
  box-shadow: 0 4px 12px rgba(0,0,0,.12);
}

.cat-search {
  width: 100%;
}

.cat-search .input-group {
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
  width: 100%;
}

.cat-search .input-group > .form-control {
  min-width: 0;
  flex: 1 1 auto;
}

.cat-search .input-group > .btn {
  flex: 0 0 34px;
  width: 34px;
  min-width: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
}

.cat-search .input-group > .input-group-text {
  flex: 0 0 34px;
  width: 34px;
  min-width: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.cat-search-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0.75rem;
  cursor: pointer;
  font-size: 0.8125rem;
  line-height: 1.4;
}

.cat-search-item:hover,
.cat-search-item--activo {
  background: #e9f0ff;
}

.cat-item-clave {
  font-weight: 600;
  white-space: nowrap;
  color: #0d6efd;
  min-width: 80px;
}

.cat-item-nombre {
  color: #333;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cat-search-empty {
  font-size: 0.8125rem;
}

/* Modal */
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-dialogo-custom {
  background: #fff;
  border-radius: 0.5rem;
  width: 600px;
  max-width: 95vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0,0,0,.2);
  overflow: hidden;
}

.modal-header-custom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: #0d6efd;
  color: #fff;
  font-size: 0.9rem;
}

.modal-body-custom {
  border-bottom: 1px solid #dee2e6;
}

.modal-tabla-wrap {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.modal-footer-custom {
  border-top: 1px solid #dee2e6;
  background: #f8f9fa;
}

.cat-modal-row {
  cursor: pointer;
}

.cat-modal-row:hover td {
  background: #e9f0ff;
}
</style>
